import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { Button, Col, Divider, Flex, Form, Row, Space, Tag, Tooltip, Typography } from "antd";
import { ContaFinanceiraComSaldoModel } from "../../../../models/contaFinanceira/contaFinanceiraComSaldoModel";
import { CloseOutlined, DollarCircleOutlined, FileTextOutlined, RedoOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { currencyBRL } from "../../../../util/dinheiroBRAuxiliar";
import { listaContaFinanceiraSorteioComSaldo, modificarSituacaoContaFinanceira } from "../../../../services/contaFinanceiraService";
import { setModalContasComsaldoResultadoSorteio } from "../../../../redux/slices/resultadoSorteio/resultadoSorteioSlice";
import { ListaContasFinanceirasAModificarModel, ModificarSituacaoContaFinanceiraModel } from "../../../../models/contaFinanceira/modificarSituacaoContaFinanceiraModel";
import dayjs from "dayjs";
import TableComponent from "../../../../components/table/tableComponent";
import { SelectedRowKeysModel } from "../../../../models/sistema/selectedRowKeysModel";

dayjs.locale("pt-br");

const ContasComSaldoModal = () => {
  //estados globais
  const resultadoSorteioState = useAppSelector((state) => state.resultadoSorteio);
  const contaFinanceiraState = useAppSelector((state) => state.contaFinanceira);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<SelectedRowKeysModel>({ keys: [], rows: [] } as SelectedRowKeysModel);

  useEffect(() => {
    dispatch(listaContaFinanceiraSorteioComSaldo(resultadoSorteioState.idSorteio ?? 0));
  }, []);

  const selecionarLinha = (value: SelectedRowKeysModel) => {
    setSelectedRowKeys(value);
  };

  const quitar = () => {
    var lista: ListaContasFinanceirasAModificarModel[] = [];
    selectedRowKeys.keys.forEach((v: number) => {
      var detalhes: ListaContasFinanceirasAModificarModel = {
        idContaFinanceira: v,
        situacao: "Quitada",
      };
      lista.push(detalhes);
    });
    var args: ModificarSituacaoContaFinanceiraModel = {
      ListaContas: lista,
    };

    dispatch(modificarSituacaoContaFinanceira({ value: args, busca: resultadoSorteioState.idSorteio ?? 0 }));
  };

  const desfazerQuitacao = () => {
    var lista: ListaContasFinanceirasAModificarModel[] = [];
    selectedRowKeys.keys.forEach((v: number) => {
      var detalhes: ListaContasFinanceirasAModificarModel = {
        idContaFinanceira: v,
        situacao: "",
      };
      lista.push(detalhes);
    });
    var args: ModificarSituacaoContaFinanceiraModel = {
      ListaContas: lista,
    };
    dispatch(modificarSituacaoContaFinanceira({ value: args, busca: resultadoSorteioState.idSorteio ?? 0 }));
  };

  const voltar = () => {
    dispatch(setModalContasComsaldoResultadoSorteio(false));
  };

  const columns: ColumnsType<ContaFinanceiraComSaldoModel> = [
    {
      title: "SITUAÇÃO",
      dataIndex: "situacao",
      filters: [
        {
          text: "Prevista",
          value: "Prevista" as any,
        },
        {
          text: "Programada",
          value: "Programada" as any,
        },
        {
          text: "Paga",
          value: "Paga" as any,
        },
      ],
      width: 90,
      onFilter: (value: any, record) => record.situacao.toString().includes(value),
      render: (_, record) => <Tag key={record.situacao}>{record.situacao}</Tag>,
    },
    {
      title: "EMISSÃO",
      dataIndex: "dataEmissao",
      align: "center",
      width: 80,
      render: (_, record) => <>{dayjs(record.dataEmissao).format("DD/MM/YYYY")}</>,
    },
    {
      title: "VENCIMENTO",
      dataIndex: "dataVencimento",
      align: "center",
      width: 90,
      render: (_, record) => <>{dayjs(record.dataVencimento).format("DD/MM/YYYY")}</>,
    },
    {
      title: "DOC.",
      dataIndex: "numeroDocumento",
      width: 80,
    },
    {
      title: "FORNECEDOR",
      dataIndex: "fornecedor",
      render: (_, record) => (
        <Typography.Text style={{ width: 200 }} ellipsis={{ tooltip: record.fornecedor }}>
          {record.fornecedor}
        </Typography.Text>
      ),
    },
    {
      title: "VALOR",
      dataIndex: "valorBruto",
      align: "right",
      width: 100,
      sorter: (a, b) => a.valorBruto - b.valorBruto,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.valorBruto),
    },
    {
      title: "VALOR LÍQUIDO",
      dataIndex: "valorLiquido",
      align: "right",
      width: 100,
      sorter: (a, b) => a.valorLiquido - b.valorLiquido,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.valorLiquido),
    },
    {
      title: "SALDO",
      dataIndex: "saldo",
      align: "right",
      sorter: (a: any, b: any) => a.saldo - b.saldo,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.saldo),
    },
    {
      title: "HISTÓRICO",
      dataIndex: "historico",
      align: "center",
      width: 100,
      render: (_, record) =>
        record.historico !== null && (
          <Tooltip placement="top" title={record.historico}>
            <Button size="small">
              <FileTextOutlined />
            </Button>
          </Tooltip>
        ),
    },
  ];

  return (
    <Flex vertical>
      <Form layout="vertical" form={form}>
        <Row gutter={8} align={"middle"}>
          <Col span={16}>
            <span>Existem contas financeiras com saldo.</span>
            <br />
            <span>Indique as contas que devem ser quitadas, indicando que o saldo das mesmas não seja considerado no resultado.</span>
          </Col>
          <Col span={8} className="buttonsCol">
            <Form.Item label=" ">
              <Tooltip title="Selecione uma conta">
                <Button icon={<DollarCircleOutlined />} onClick={quitar} disabled={selectedRowKeys.keys.length === 0}>
                  QUITAR
                </Button>
              </Tooltip>
            </Form.Item>
            <Form.Item label=" ">
              <Tooltip title="Selecione uma conta">
                <Button icon={<RedoOutlined />} onClick={desfazerQuitacao} disabled={selectedRowKeys.keys.length === 0}>
                  DESFAZER QUITAÇÃO
                </Button>
              </Tooltip>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row gutter={8} align={"middle"}>
        <Col span={24}>
          <TableComponent
            data={contaFinanceiraState.listContasComSaldo}
            columns={columns}
            loading={contaFinanceiraState.loadingContasComSaldo}
            selectionType="checkbox"
            rowSelection={selecionarLinha}
            tableProps={{
              onRow: (record: ContaFinanceiraComSaldoModel) => {
                return {
                  onClick: () => {
                    selecionarLinha({ keys: [record.id], rows: [record] });
                  },
                };
              },
              selectedRowKeys: selectedRowKeys.keys,
            }}
          />
        </Col>
      </Row>
      <Divider />
      <Row gutter={8} align={"middle"}>
        <Col span={24} className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<CloseOutlined />}>
              FECHAR
            </Button>
          </Space>
        </Col>
      </Row>
    </Flex>
  );
};

export default ContasComSaldoModal;
